// packages
'use client';

import React, { useEffect, useState } from 'react';
import { useBlockingRouter } from '../../hooks/useBlockingRouter';
import { useDispatch, useSelector } from 'react-redux';
import NProgress from 'nprogress';
import { useSearchParams } from 'next/navigation';

// markup
import { Alerts } from '../Alerts';
import MuiStripeVerificationModal from '../../mui/MuiModal/MuiStripeVerificationModal/index';
import SubscriptionFailedModal from '../SubscriptionChargeFailedModal/index';
import Confirm from '../Confirm/index';
import { LoadingAnimation } from '../../blocks/LoadingAnimation';
import { MobileMenuContainer } from './Header/MobileMenu/MobileMenuContainer';

import { SideNavLayoutContainer } from './SideNav/SideNavLayout/SideNavLayoutContainer';
import StickyHeaderLayout from '../../mui/layouts/StickyHeaderLayout/StickyHeaderLayout';

//logic
import { displayStripeNeedsModal } from '../../redux/actions';
import Globals from '../../lib/Globals';
import { hideSideNav, hideStickyHeader } from './helpers';
import { FlattenedUser } from '../../types/user/index';
import { SubscriptionStatus, UserType } from '#types';
import {
	hasActiveSubscription,
	hasIncompleteOrPastDueSubscription,
} from '../../lib/helpers';
import { SubscriptionTrialEnding } from '../SubscriptionTrialEnding';

NProgress.configure( { showSpinner: false } );

export const theme = {
	// Main palette
	primary: '#243D32',
	secondary: '#B4BE9C',
	gray: 'rgba(0,0,0,0.3)',
	lightGray: 'rgba(0, 0, 0, 0.03)',
	mediumGray: 'rgba( 238, 237, 240, 1)',
	darkGray: 'rgb(196,196,196)',
	white: '#FFFFFF',
	black: '#000000',
	readableGray: '#949494', // this is better than black with opacity
	creamAccent: '#F0DDAF',

	//View Sizes
	tablet: 768,
	desktop: 960,

	//TODO REMOVE THE BELOW
	lightGreen: '#D5ECE1',

	// Message and Error handling
	error: '#CC0000',
	warning: '#FBBC05',
	success: '#34A853',

	// Shades
	lightestShade: '#F9F9F9',
	lightShade: '#EAF1F4',
	middleShade: '#E7E9E8',
	darkerShade: '#848a87',
	darkShade: '#494B4A',

	// Border
	lightBorderColor: '#E1E1E1',
	lightBorder: '1px solid rgba(207, 206, 207, 0.37)',
	errorBorder: '1px solid #ff0000',

	// Breakpoints
	xlargeScreen: 1140,
	largeScreen: 850,
	mediumScreen: 640,
	minWidth: 320,

	// drop-shadows
	boxshadowBasic:
		'0px 0 8px rgba(221, 221, 221, 0.15), 30px 30px 60px rgba(221, 221, 221, 0.25)',
};

/**
 * Page wrapper - wrapper for all page components containing top level data
 *
 * Feature Flag
 */
export const Page: React.FC<{ children: React.ReactNode }> = ( { children } ) => {
	const user: FlattenedUser = useSelector( ( state: any ) => state.user );
	const loading: boolean = useSelector( ( state: any ) => state.loader.loading );
	const searchParams = useSearchParams();
	const confirm:
		| {
				active: boolean;
				props: {
					title: string;
					message: string;
					options: {
						text: string;
						className?: string;
						callback: () => void;
						loading?: boolean;
						dataCy?: string;
					}[];
					confirm?: () => void;
				} | null;
		  }
		| undefined = useSelector( ( state: any ) => state.confirm );
	const overlay: boolean = useSelector(
		( state: any ) => state.loadingOverlay.overlay
	);
	const stripeNeedsModal: { active: boolean; props: any } | undefined =
		useSelector( ( state: any ) => state.stripeNeedsModal );
	const router = useBlockingRouter();
	const dispatch = useDispatch();
	const vendorName =
		user?.isLoggedIn && user.userType === UserType.OrgUser
			? /* though technically incorrect, in practice after onboarding `organization.name`
			is always defined. But not during onboarding. Hence the `?` here */
			  user.organization?.name
			: undefined;

	const orgUser =
		user?.isLoggedIn && user.userType === UserType.OrgUser ? user : undefined;
	const currentRoute = router.pathname;
	const currentSearch = searchParams?.toString() || '';

	const [ subscriptionFailedModalOpen, setSubscriptionFailedModalOpen ] =
		useState( false );
	const [ showMobileMenu, setShowMobileMenu ] = useState( false );

	useEffect( () => {
		if (
			orgUser?.organization?.subscription?.status ===
				SubscriptionStatus.Incomplete &&
			currentRoute === '/'
		) {
			setSubscriptionFailedModalOpen( true );
		}
	}, [ orgUser?.organization?.subscription?.status, currentRoute ] );

	useEffect( () => {
		const onBeforeUnload = () => NProgress.done();
		const onAnchorClick = ( event: MouseEvent ) => {
			const target = event.target;
			if ( !( target instanceof Element ) ) {
				return;
			}

			const anchor = target.closest( 'a[href]' );
			if ( !( anchor instanceof HTMLAnchorElement ) ) {
				return;
			}

			if (
				event.defaultPrevented ||
				event.button !== 0 ||
				event.metaKey ||
				event.ctrlKey ||
				event.shiftKey ||
				event.altKey ||
				anchor.target === '_blank' ||
				anchor.hasAttribute( 'download' )
			) {
				return;
			}

			const href = anchor.href;
			if ( !href ) {
				return;
			}

			const nextUrl = new URL( href, window.location.href );
			const currentUrl = new URL( window.location.href );

			if ( nextUrl.origin !== currentUrl.origin ) {
				return;
			}

			if ( nextUrl.href === currentUrl.href ) {
				return;
			}

			NProgress.start();
		};

		const onRouteChangeStart = () => NProgress.start();
		const onRouteChangeComplete = () => NProgress.done();
		const onRouteChangeError = () => NProgress.done();

		window.addEventListener( 'beforeunload', onBeforeUnload );
		document.addEventListener( 'click', onAnchorClick, true );
		router.events.on( 'routeChangeStart', onRouteChangeStart );
		router.events.on( 'routeChangeComplete', onRouteChangeComplete );
		router.events.on( 'routeChangeError', onRouteChangeError );

		return () => {
			window.removeEventListener( 'beforeunload', onBeforeUnload );
			document.removeEventListener( 'click', onAnchorClick, true );
			router.events.off( 'routeChangeStart', onRouteChangeStart );
			router.events.off( 'routeChangeComplete', onRouteChangeComplete );
			router.events.off( 'routeChangeError', onRouteChangeError );
		};
	}, [ router.events ] );

	useEffect( () => {
		NProgress.done();
	}, [ currentRoute, currentSearch ] );

	useEffect( () => {
		if ( loading ) {
			NProgress.start();
		} else {
			NProgress.done();
		}
	}, [ loading ] );

	// Because of the vendor dashboard fixed header we need to show the subscription alert in that component and not here
	const willShowSubscriptionAlert =
		user?.isLoggedIn &&
		user.userType === UserType.OrgUser &&
		currentRoute === '/' &&
		( hasIncompleteOrPastDueSubscription( user ) || !hasActiveSubscription( user ) );

	const pageContent = (
		<SideNavLayoutContainer hideSideNav={ hideSideNav( currentRoute ) }>
			<>
				{ willShowSubscriptionAlert ? null : (
					<Alerts currentRoute={ currentRoute } gutterTop />
				) }
				<SubscriptionTrialEnding />
				{ children }
				<MuiStripeVerificationModal
					open={ stripeNeedsModal?.active }
					closeModal={ () =>
						dispatch(
							displayStripeNeedsModal( {
								props: null,
								active: false,
							} )
						)
					}
					vendorName={ vendorName }
					{ ...stripeNeedsModal?.props }
				/>
				{ subscriptionFailedModalOpen ? (
					<SubscriptionFailedModal
						closeModal={ () => setSubscriptionFailedModalOpen( false ) }
					/>
				) : null }
				{ confirm?.active ? <Confirm /> : null }
				<div id={ Globals.rootPortalId } />
				{ user?.isLoggedIn ? (
					<MobileMenuContainer
						active={ showMobileMenu }
						closeMobileMenu={ () => setShowMobileMenu( false ) }
					/>
				) : null }
			</>
			{ overlay ? (
				<LoadingAnimation
					cover={ true }
					transparent={ true }
					width='12rem'
					coverColor={ 'lightBlue' }
				/>
			) : null }
		</SideNavLayoutContainer>
	);

	return hideStickyHeader( currentRoute ) ? (
		<>{ pageContent }</>
	) : (
		<>
			<StickyHeaderLayout
				isActiveMobileMenu={ showMobileMenu }
				toggleMobileMenu={ setShowMobileMenu }
			>
				{ pageContent }
			</StickyHeaderLayout>
		</>
	);
};
